class TieredQuantity extends HTMLElement {
  connectedCallback() {
    if (this._inited) return;

    // elements
    this.input = this.querySelector('.quantity__input');
    this.msg   = this.querySelector('[data-tier-savings-message]');
    if (!this.input || !this.msg) return;

    // data
    this.basePrice  = this._toInt(this.dataset.basePrice);
    this.cartQty    = this._toInt(this.dataset.cartQty);
    this.unitSing   = this.dataset.unitSingular || 'item';
    this.unitPlur   = this.dataset.unitPlural || 'items';
    this.template   = this.dataset.message || '';
    this.tiers      = this._parseTiers(this.dataset.tiers);

    // pre-sort tiers (lowest qty -> highest)
    this.tiers.sort((a, b) => a.qty - b.qty);

    // gets the price blocks on the PDP
    this.priceEls = Array.from(document.querySelectorAll('.product__price-block .price-item span'));
    this.priceEls.forEach(el => {
      if (!el.dataset.originalPriceCents) {
        const cents = this._extractCentsFromText(el.textContent);
        if (cents > 0) el.dataset.originalPriceCents = String(cents);
      }
    });

    this._onAnyChange = () => this._update();
    this.input.addEventListener('input', this._onAnyChange, {passive:true});
    this.input.addEventListener('change', this._onAnyChange, {passive:true});

    const qtyContainer = this.input.closest('quantity-input');
    qtyContainer?.addEventListener('click', (e) => {
      if (!e.target.closest?.('.quantity__button')) return;
      queueMicrotask(this._onAnyChange);
    }, {passive:true});

    this._update();
    this._inited = true;
  }

  disconnectedCallback() {
    if (!this._inited) return;
    this.input.removeEventListener('input', this._onAnyChange);
    this.input.removeEventListener('change', this._onAnyChange);
    this._inited = false;
  }

  _update() {
    const inputQty = Math.max(0, this._toInt(this.input.value));
    const totalQty = inputQty + this.cartQty;
    const currentUnit = totalQty > 0 ? this._priceForQty(totalQty) : this.basePrice;
    this._setPriceDisplay(currentUnit); // reflect tier in PDP price

    if (!this.tiers.length || totalQty <= 0) return this._setMessage('');

    const nextTier = this._nextCheaperTier(totalQty, currentUnit);
    if (!nextTier) return this._setMessage('');

    let itemsNeeded = Math.max(0, nextTier.qty - totalQty);
    if (itemsNeeded <= 0) {
      // already at that threshold, find the following cheaper tier
      const followingTier = this._nextCheaperTier(nextTier.qty, currentUnit);
      if (!followingTier) return this._setMessage('');
      itemsNeeded = Math.max(0, followingTier.qty - totalQty);
      const savePerItem = Math.max(0, this.basePrice - followingTier.price);
      return savePerItem > 0 ? this._render(itemsNeeded, savePerItem) : this._setMessage('');
    }

    const savePerItem = Math.max(0, this.basePrice - nextTier.price);
    return savePerItem > 0 ? this._render(itemsNeeded, savePerItem) : this._setMessage('');
  }

  _priceForQty(qty) {
    let price = this.basePrice;
    for (let i = 0; i < this.tiers.length; i++) {
      if (qty >= this.tiers[i].qty) price = this._resolvePrice(this.tiers[i].price);
      else break;
    }
    return price;
  }

  _nextCheaperTier(currentQty, currentPrice) {
    for (let i = 0; i < this.tiers.length; i++) {
      const tier = this.tiers[i];
      if (tier.qty > currentQty && this._resolvePrice(tier.price) < currentPrice) return tier;
    }
    return null;
  }

  _render(itemsNeeded, savingPerItemCents) {
    const amount = this._formatMoney(savingPerItemCents);
    const itemLabel = itemsNeeded === 1 ? this.unitSing : this.unitPlur;

    const html = this.template && this.template.includes('{{')
      ? this.template
          .replace(/\{\{\s*count\s*\}\}/g, String(itemsNeeded))
          .replace(/\{\{\s*item_label\s*\}\}/g, itemLabel)
          .replace(/\{\{\s*amount\s*\}\}/g, amount)
          .replace(/\{\{\s*unit_label\s*\}\}/g, this.unitSing)
      : '';

    this._setMessage(html);
  }

  _setMessage(html) {
    const show = Boolean(html && String(html).trim().length);
    this.msg.hidden = !show;
    this.msg.innerHTML = show ? html : '';
  }

  _parseTiers(jsonStr) {
    try {
      const arr = JSON.parse(jsonStr || '[]');
      return Array.isArray(arr)
        ? arr
            .map(t => ({ qty: this._toInt(t.qty), price: this._toInt(t.price) }))
            .filter(t => t.qty > 0)
        : [];
    } catch { return []; }
  }

  _resolvePrice(v) { return typeof v === 'number' && v > 0 ? v : this.basePrice; }
  _toInt(v) { return parseInt(v ?? '0', 10) || 0; }

  _formatMoney(cents) {
    const rounded = Math.round(cents);
    if (window.Shopify?.formatMoney) {
      const fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
      return window.Shopify.formatMoney(rounded, fmt);
    }
    const currency = window.Shopify?.currency?.active || 'USD';
    return (rounded / 100).toLocaleString(undefined, { style: 'currency', currency });
  }

  _setPriceDisplay(unitPriceCents) {
    if (!this.priceEls?.length) return;
    const formatted = this._formatMoney(unitPriceCents);
    this.priceEls.forEach(el => {
      // If your theme uses "sale"/"compare" wrappers, consider updating those too.
      el.textContent = formatted;
      el.classList.toggle('is-tiered-price', unitPriceCents < (this.basePrice ?? this._getOriginalCents(el)));
      el.setAttribute('aria-live', 'polite');
    });
  }

  _restoreBasePrice() {
    if (!this.priceEls?.length) return;
    this.priceEls.forEach(el => {
      const cents = this._getOriginalCents(el) || this.basePrice;
      el.textContent = this._formatMoney(cents);
      el.classList.remove('is-tiered-price');
    });
  }

  _getOriginalCents(el) {
    const d = el?.dataset?.originalPriceCents;
    return d ? this._toInt(d) : 0;
  }

  _extractCentsFromText(txt) {
    const nums = String(txt || '').replace(/[^\d.]/g,'');
    if (!nums) return 0;
    const value = Number(nums);
    // Assume two decimals when a dot exists; safe for most locales once we take over formatting.
    return Math.round(value * 100);
  }
}

customElements.get('tiered-quantity') || customElements.define('tiered-quantity', TieredQuantity);
